import { Component, ComponentInterface, Element, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { SelectArrow } from '../select/select-arrow';
import {
    WcsButtonMode,
    WcsButtonShape,
    WcsButtonSize,
} from '../button/button-interface';
import { createPopper, Instance } from '@popperjs/core';
import { WcsDropdownPlacement } from './dropdown-interface';
import {
    clickTargetIsElementOrChildren,
    inheritAriaAttributes,
    inheritAttributes,
    setOrRemoveAttribute
} from '../../utils/helpers';
import { getActionForKeyboardEvent, KeyboardEventAssociatedAction } from "./dropdown-keyboard-event";
import { AriaAttributeName, MutableAriaAttribute } from "../../utils/mutable-aria-attribute";

const DROPDOWN_INHERITED_ATTRS = ['tabindex'];

/**
 * The dropdown component use a wcs-button under the hood, so you can use the same css classes as the button to style the
 * dropdown.
 * 
 * @slot placeholder - The slot containing the placeholder displayed in button
 * @slot item - The slot container the list of `wcs-dropdown-item` elements
 * 
 * @cssprop --wcs-dropdown-outline-color-focus - Outline color of dropdown when focus it
 * 
 * @cssprop --wcs-dropdown-plain-arrow-color - Color of the arrow indicator when the dropdown is plain
 * @cssprop --wcs-dropdown-stroked-arrow-color - Color of the arrow indicator when the dropdown is stroked
 * @cssprop --wcs-dropdown-clear-arrow-color - Color of the arrow indicator when the dropdown is not filled
 * @cssprop --wcs-dropdown-arrow-color-disabled - Base color of the arrow indicator when the dropdown is disabled
 * 
 * @cssprop --wcs-dropdown-padding-empty - Padding of and empty dropdown
 * 
 * @cssprop --wcs-dropdown-overlay-padding - Defines the vertical padding inside the dropdown overlay
 * @cssprop --wcs-dropdown-overlay-max-height - Specifies the maximum height of the dropdown overlay
 * @cssprop --wcs-dropdown-overlay-background-color - Determines the background color of the dropdown overlay
 * @cssprop --wcs-dropdown-overlay-border-width - Sets the border width of the dropdown overlay
 * @cssprop --wcs-dropdown-overlay-border-radius - Defines the border radius of the dropdown overlay
 * @cssprop --wcs-dropdown-overlay-border-color - Specifies the border color of the dropdown overlay
 */
@Component({
    tag: 'wcs-dropdown',
    styleUrl: 'dropdown.scss',
    shadow: {
        delegatesFocus: true
    }
})
export class Dropdown implements ComponentInterface, MutableAriaAttribute {
    @Element() private el: HTMLWcsDropdownElement;
    private inheritedAttributes: { [k: string]: any } = {};

    /**
     * This button is visually hidden. It used to carry all aria attributes and acts as the controller of the menu.
     * The wcs-button could not handle the aria-controls and aria-activedescendant attributes because of the IDREF
     * not passing from a shadow-dom through another.
     * @private
     */
    private nativeButton!: HTMLButtonElement;

    /**
     * This button is shown on the user interface, and visually receives focus and catch click events.  
     * It is hidden to screen readers since the native button carries all the aria informations.
     * @private
     */
    private wcsButton!: HTMLWcsButtonElement;

    /**
     * This div is shown on the user interface when the dropdown is expanded.
     * @private
     */
    private popoverDiv!: HTMLDivElement;

    /** Hides the arrow in the button */
    @Prop({ reflect: true }) noArrow: boolean = false;

    /** Dropdown's button mode */
    @Prop() mode: WcsButtonMode = 'stroked';

    /** Dropdown's button shape */
    @Prop() shape: WcsButtonShape = 'normal';
    
    /** Dropdown's button size */
    @Prop() size: WcsButtonSize = 'm';

    /** Specifies whether the dropdown button is clickable or not */
    @Prop({ reflect: true }) disabled: boolean = false;

    /** placement of the dropdown's popover */
    @Prop() placement: WcsDropdownPlacement = 'bottom-end';

    @State() private expanded: boolean = false;


    private popper: Instance;
    
    private lastFocusedItemElement: HTMLWcsDropdownItemElement | null;
    
    private get items(): HTMLWcsDropdownItemElement[] {
        return Array.from(this.el.querySelectorAll('wcs-dropdown-item'));
    }

    @Watch('placement')
    protected placementChange() {
        this.popper.setOptions({
            ...this.popper.state.options,
            placement: this.placement
        }).then(_ => this.popper.update());
    }
    
    @Listen('blur')
    onBlur() {
        this.clearLastFocusedItem();
        this.expanded = false;
    }

    componentDidLoad() {
        this.popper = createPopper(this.wcsButton, this.popoverDiv, {
            placement: this.placement,
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 8]
                    }
                }
            ]
        });

        this.fixForFirefoxBelow63();
    }

    private fixForFirefoxBelow63() {
        // If the items appear out of the slot we place them back
        const items = this.el.querySelectorAll('wcs-dropdown-item');
        const container = this.el.querySelector('.container');
        if (items.length > 0 && container) {
            items.forEach(i => {
                this.el.removeChild(i);
                container.appendChild(i);
            });
        }
    }

    private onButtonClick(_: MouseEvent): void {
        this.clearLastFocusedItem();
        this.expanded = !this.expanded;
        
        if (this.expanded) {
            this.focusItem(0);
        }
    }

    @Listen('click', {target: 'window'})
    onWindowClickEvent(event: MouseEvent) {
        const clickedOnDropdownOrChildren = clickTargetIsElementOrChildren(event, this.el);
        if (this.expanded && !clickedOnDropdownOrChildren) {
            this.expanded = false;
        }
    }

    @Listen('wcsDropdownItemClick')
    dropdownItemClick(_: CustomEvent<void>) {
        this.expanded = false;
        this.nativeButton.focus();
    }

    @Listen('keydown')
    onKeyDown(_event: KeyboardEvent): void {
        const currentState = this.expanded ? 'opened' : 'closed';
        const actionsFromKeyboardEvents: KeyboardEventAssociatedAction[] = getActionForKeyboardEvent(_event, currentState);

        // If we have at least one associated actions, we prevent the default behavior of the event. 
        // Except if the action is a focus move (we have to handle the preventDefault behavior ourselves in the action implementation)
        if (actionsFromKeyboardEvents.length != 0) {
            _event.preventDefault();
        }

        for (const actionFromKeyboardEvent of actionsFromKeyboardEvents) {
            this.doActionFromKeyboardEventAssociatedAction(actionFromKeyboardEvent);
        }
    }
    
    @Method()
    async setAriaAttribute(attr: AriaAttributeName, value: string | null | undefined) {
        setOrRemoveAttribute(this.nativeButton, attr, value);
    }

    doActionFromKeyboardEventAssociatedAction(actionFromKeyboardEvent: KeyboardEventAssociatedAction) {
        switch (actionFromKeyboardEvent.kind) {
            case 'OpenMenu':
                this.expanded = true;
                this.focusItem(0);
                break;
            case 'CloseMenu':
                this.expanded = false;
                this.nativeButton.focus();
                break;
            case 'ClearFocus':
                this.clearLastFocusedItem();
                break;
            case 'FocusItem':
                switch (actionFromKeyboardEvent.target) {
                    case 'previous':
                        this.focusClosestItem('previous');
                        break;
                    case 'next':
                        this.focusClosestItem('next');
                        break;
                    case 'first':
                        this.focusItem(0);
                        break;
                    case 'last':
                        this.focusItem(this.items.length - 1);
                        break;
                    default:
                        break;
                }
                break;
            default:
                break;
        }
    }

    /**
     * Removes the focus and tabindex 0 of the last focused item if any.
     * @private
     */
    private clearLastFocusedItem() {
        if (this.lastFocusedItemElement) {
            this.lastFocusedItemElement.tabIndex = -1;
            this.lastFocusedItemElement = null;
        }
    }
    
    private focusItem(indexToFocus: number) {
        this.clearLastFocusedItem();
        this.lastFocusedItemElement = this.items[indexToFocus];
        if (this.lastFocusedItemElement) {
            this.lastFocusedItemElement.tabIndex = 0;
            requestAnimationFrame(() => {
                this.lastFocusedItemElement?.focus();
            });
        }
    }
    
    private focusClosestItem(direction: 'next' | 'previous'): void {
        const indexToFocus = this.getClosestActiveItemIndexForDirection(direction);
        if (indexToFocus === -1) return;
        
        this.focusItem(indexToFocus);
    }
    
    private getClosestActiveItemIndexForDirection(direction: 'next' | 'previous'): number {
        const items = this.items;
        let currentIndex = Array.from(items).indexOf(this.lastFocusedItemElement);

        const MIN_INDEX = 0;
        const MAX_INDEX = items.length - 1;

        if (direction === 'next' && currentIndex < MAX_INDEX) {
            currentIndex++;
        } else if (direction === 'previous' && currentIndex > MIN_INDEX) {
            currentIndex--;
        } else {
            // Used to scroll through items infinitely with keyboard
            if (direction === 'next' && currentIndex >= MAX_INDEX) {
                currentIndex = 0;
            }
            if (direction === 'previous' && currentIndex === MIN_INDEX) {
                currentIndex = MAX_INDEX;
            }
        }
        
        return currentIndex;
    }

    componentDidRender() {
        if (this.popper) {
            this.popper.update();
        }
    }

    componentWillLoad(): Promise<void> | void {
        this.inheritedAttributes = {
            ...inheritAriaAttributes(this.el),
            ...inheritAttributes(this.el, DROPDOWN_INHERITED_ATTRS),
        };
    }
    
    render() {
        return (
            <Host>
                <button id="dropdown-button"
                        aria-controls="menu"
                        disabled={this.disabled}
                        aria-haspopup="true"
                        role="button"
                        aria-expanded={this.expanded ? 'true' : 'false'}
                        ref={el => this.nativeButton = el}
                        onClick={($event) => this.onButtonClick($event)}
                        onBlur={($event) => $event.stopImmediatePropagation()}
                        {...this.inheritedAttributes}>Dropdown</button>
                <wcs-button mode={this.mode} shape={this.shape} disabled={this.disabled} size={this.size}
                            ref={el => this.wcsButton = el}
                            aria-hidden="true"
                            tabindex={-1}
                            onClick={($event) => this.onButtonClick($event)}
                            onBlur={(e) => e.stopImmediatePropagation()}>
                    <div class="wcs-button-content-wrapper">
                        <slot name="placeholder"/>
                        {this.noArrow ? null : (<SelectArrow up={this.expanded}/>)}
                    </div>
                </wcs-button>
                <div class={(this.expanded ? 'show ' : '') + 'popover'}
                     id="menu"
                     role="menu"
                     aria-labelledby="dropdown-button"
                     tabindex={-1}
                     ref={el => this.popoverDiv = el}>
                    <div role="presentation" id="arrow" data-popper-arrow/>
                    <div role="presentation" class="container">
                        <slot name="item">
                            <span id={"is-empty"}></span>
                        </slot>
                    </div>
                </div>
            </Host>
        );
    }
}

